import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("feral_admin_token")}` } });

const Stat = ({ label, value }) => (
  <div className="bg-white border border-[#eee3d2] rounded-lg p-4">
    <p className="text-[#9a876f] text-xs uppercase tracking-[0.12em] mb-1">{label}</p>
    <p className="feral-serif text-3xl text-[#9b3d0a]">{value ?? "—"}</p>
  </div>
);

const AdminInnovators = () => {
  const [summary, setSummary] = useState(null);
  const [invites, setInvites] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("requests");
  const [form, setForm] = useState({ name: "", email: "", mobile: "", source: "", invited_by: "", send_email: true });
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, i, r] = await Promise.all([
        axios.get(`${API}/admin/innovators/summary`, auth()),
        axios.get(`${API}/admin/innovators/invites`, auth()),
        axios.get(`${API}/admin/innovators/requests`, auth()),
      ]);
      setSummary(s.data); setInvites(i.data); setRequests(r.data);
    } catch { /* not logged in */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setMsg("");
    if (!form.name.trim() || !form.email.trim()) { setMsg("Name and email required"); return; }
    try {
      const { data } = await axios.post(`${API}/admin/innovators/invite`, form, auth());
      setMsg(`Invite created. Link: ${window.location.origin}${data.link}`);
      setForm({ name: "", email: "", mobile: "", source: "", invited_by: "", send_email: true });
      load();
    } catch (e2) { setMsg(e2?.response?.data?.detail || "Failed"); }
  };

  const action = async (id, act) => { await axios.post(`${API}/admin/innovators/invite/${id}/${act}`, {}, auth()); load(); };
  const review = async (r, decision) => {
    const verb = decision === "approve" ? "Accept" : "Reject";
    if (!window.confirm(`${verb} ${r.name}? An email will be sent to ${r.email}${decision === "approve" ? " with their booking link" : ""}.`)) return;
    await axios.post(`${API}/admin/innovators/request/${r.id}/${decision}`, {}, auth());
    setMsg(`${r.name} ${decision === "approve" ? "accepted — booking link emailed" : "rejected — email sent"}.`);
    load();
  };
  const bookingLink = `${window.location.origin}/events/feral-innovators/book`;
  const copy = (link) => navigator.clipboard?.writeText(`${window.location.origin}${link}`);

  return (
    <div className="min-h-screen bg-[#f6efe4] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="feral-serif text-3xl text-[#9b3d0a]">FERAL Innovators</h1>
          <Link to="/admin" className="text-sm text-[#9b3d0a] underline">← Dashboard</Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
          <Stat label="Capacity" value={summary?.capacity} />
          <Stat label="Confirmed" value={summary?.booked} />
          <Stat label="Remaining" value={summary?.remaining} />
          <Stat label="Invited" value={summary?.invited} />
          <Stat label="Not booked" value={summary?.not_booked} />
          <Stat label="Requests" value={summary?.requests} />
          <Stat label="Accommodation" value={summary?.accommodation} />
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="room-stock">
          {Object.entries(summary?.rooms || {}).map(([k, r]) => (
            <Stat key={k} label={`${r.label} · ${r.quantity} total`} value={`${r.remaining} left`} />
          ))}
        </div>

        <div className="bg-white border border-[#eee3d2] rounded-lg p-5 mb-6 flex flex-wrap items-center gap-3" data-testid="private-booking-link">
          <span className="text-xs uppercase tracking-[0.12em] text-[#9a876f]">Private booking link</span>
          <code className="text-sm text-[#9b3d0a] break-all flex-1">{bookingLink}</code>
          <button onClick={() => { navigator.clipboard?.writeText(bookingLink); setMsg("Private booking link copied."); }} className="bg-[#9b3d0a] text-white rounded-full px-4 py-1.5 text-xs font-semibold" data-testid="copy-booking-link">Copy</button>
        </div>

        <form onSubmit={create} className="bg-white border border-[#eee3d2] rounded-lg p-5 mb-8 grid md:grid-cols-6 gap-3 items-end">
          <input className="border border-[#d9c3a8] rounded px-3 py-2 text-sm" placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" />
          <input className="border border-[#d9c3a8] rounded px-3 py-2 text-sm" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inv-email" />
          <input className="border border-[#d9c3a8] rounded px-3 py-2 text-sm" placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
          <input className="border border-[#d9c3a8] rounded px-3 py-2 text-sm" placeholder="Invited by" value={form.invited_by} onChange={(e) => setForm({ ...form, invited_by: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-[#6e5646]"><input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} /> Email invite</label>
          <button className="bg-[#9b3d0a] text-white rounded-full py-2.5 text-sm font-semibold" data-testid="inv-create">Add &amp; invite</button>
        </form>
        {msg && <p className="text-sm text-[#9b3d0a] mb-6 break-all">{msg}</p>}

        <div className="flex gap-3 mb-4">
          <button onClick={() => setTab("invites")} className={`px-4 py-2 rounded-full text-sm ${tab === "invites" ? "bg-[#9b3d0a] text-white" : "bg-white border border-[#e0d3c0]"}`}>Invites ({invites.length})</button>
          <button onClick={() => setTab("requests")} data-testid="tab-requests" className={`px-4 py-2 rounded-full text-sm ${tab === "requests" ? "bg-[#9b3d0a] text-white" : "bg-white border border-[#e0d3c0]"}`}>Requests to review ({requests.filter((r) => r.status === "requested").length})</button>
        </div>

        {tab === "invites" && (
          <div className="bg-white border border-[#eee3d2] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f3e7d6] text-[#7a6a58] text-xs uppercase"><tr>
                {["Name", "Email", "Status", "Amount", "Room", "Photo OK", "Link", "Actions"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
              </tr></thead>
              <tbody>
                {invites.map((v) => (
                  <tr key={v.id} className="border-t border-[#f0e6d7]">
                    <td className="px-3 py-2">{v.name}</td>
                    <td className="px-3 py-2">{v.email}</td>
                    <td className="px-3 py-2"><span className="uppercase text-xs font-semibold">{v.status}</span></td>
                    <td className="px-3 py-2">{v.amount ? `£${v.amount}` : "—"}</td>
                    <td className="px-3 py-2">{v.accommodation_label || "—"}</td>
                    <td className="px-3 py-2" data-testid={`photo-consent-${v.id}`}>{v.status === "booked" ? (v.photo_consent ? "Yes" : "No") : "—"}</td>
                    <td className="px-3 py-2"><button onClick={() => copy(v.link)} className="text-[#9b3d0a] underline text-xs">Copy</button></td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button onClick={() => action(v.id, "resend")} className="text-xs text-[#6e5646] mr-2">Resend</button>
                      <button onClick={() => action(v.id, "revoke")} className="text-xs text-[#b05a2a] mr-2">Revoke</button>
                      <button onClick={() => action(v.id, "cancel")} className="text-xs text-[#9a3a3a]">Cancel</button>
                    </td>
                  </tr>
                ))}
                {!invites.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-[#9a876f]">No invites yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "requests" && (
          <div className="bg-white border border-[#eee3d2] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f3e7d6] text-[#7a6a58] text-xs uppercase"><tr>
                {["Name / contact", "Business", "Based", "Building", "Links / notes", "Decision"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
              </tr></thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-t border-[#f0e6d7] align-top">
                    <td className="px-3 py-2"><p className="font-semibold">{r.name}</p><p className="text-xs text-[#7a6a58]">{r.email}</p><p className="text-xs text-[#7a6a58]">{r.mobile}</p></td>
                    <td className="px-3 py-2">{r.business}</td>
                    <td className="px-3 py-2">{r.based}</td>
                    <td className="px-3 py-2 max-w-xs">{r.building}</td>
                    <td className="px-3 py-2 max-w-xs text-xs text-[#6e5646] break-words">
                      {[r.website, r.linkedin, r.instagram].filter(Boolean).map((l) => <p key={l}>{l}</p>)}
                      {r.referrer && <p>Referred by: {r.referrer}</p>}
                      {r.notes && <p className="italic">{r.notes}</p>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" data-testid={`request-decision-${r.id}`}>
                      {r.status === "invited" && <span className="text-xs font-semibold text-[#5b8049]">Accepted · link sent</span>}
                      {r.status === "rejected" && <span className="text-xs font-semibold text-[#9a3a3a]">Rejected · email sent</span>}
                      {r.status === "requested" && (<>
                        <button onClick={() => review(r, "approve")} data-testid={`accept-${r.id}`} className="bg-[#5b8049] text-white rounded-full px-3 py-1 text-xs mr-2">✓ Accept</button>
                        <button onClick={() => review(r, "reject")} data-testid={`reject-${r.id}`} className="bg-[#9a3a3a] text-white rounded-full px-3 py-1 text-xs">✕ Reject</button>
                      </>)}
                    </td>
                  </tr>
                ))}
                {!requests.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-[#9a876f]">No requests yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInnovators;
